import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon } from '@/shared/ui/icons';

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";

// Strips generated debug props to prevent React unknown-prop warnings
const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  if (!props) return clean;
  for (const key in props) {
    const normalizedKey = String(key).toLowerCase();
    if (
      !normalizedKey.startsWith("_fg") &&
      !normalizedKey.startsWith("figma") &&
      !normalizedKey.startsWith("node-") &&
      normalizedKey !== "figma_selection_id"
    ) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const RadixDropdownMenu = DropdownMenuPrimitive;

const hasRadixDropdownMenu =
  typeof RadixDropdownMenu?.Root !== "undefined" &&
  typeof RadixDropdownMenu?.Trigger !== "undefined" &&
  typeof RadixDropdownMenu?.Content !== "undefined";

let hasWarnedMissingRadixDropdownMenu = false;
const warnMissingRadixDropdownMenu = () => {
  if (hasWarnedMissingRadixDropdownMenu) return;
  hasWarnedMissingRadixDropdownMenu = true;
  console.warn(
    "[ui/dropdown-menu] Radix DropdownMenu primitives unavailable. Falling back to basic markup.",
  );
};

const fullscreenPortalContainer = (): HTMLElement | undefined =>
  typeof document !== "undefined" && document.fullscreenElement instanceof HTMLElement
    ? document.fullscreenElement
    : undefined;

const sanitizeTriggerChild = (node: React.ReactNode): React.ReactNode => {
  if (!React.isValidElement(node)) {
    return node;
  }

  // Clone the element with filtered props instead of recreating it
  // This preserves the element type while removing generated debug props
  const cleanProps = filterGeneratedProps((node.props ?? {}) as Record<string, unknown>);

  return React.cloneElement(node, cleanProps as any);
};

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  if (!hasRadixDropdownMenu) {
    warnMissingRadixDropdownMenu();
    const { open, defaultOpen, onOpenChange, modal, dir, ...rest } = props as any;
    return <div data-slot="dropdown-menu-fallback" {...filterGeneratedProps(rest)} />;
  }
  const cleanProps = filterGeneratedProps(props);
  return <RadixDropdownMenu.Root data-slot="dropdown-menu" {...cleanProps} />;
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  if (!hasRadixDropdownMenu) {
    warnMissingRadixDropdownMenu();
    return null;
  }
  const cleanProps = filterGeneratedProps(props);
  return <RadixDropdownMenu.Portal data-slot="dropdown-menu-portal" {...cleanProps} />;
}

const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>
>(({ children, asChild, ...props }: any, ref: any) => {
  if (!hasRadixDropdownMenu) {
    warnMissingRadixDropdownMenu();
    const { disabled, ...rest } = filterGeneratedProps(props);
    return (
      <span
        data-slot="dropdown-menu-trigger-fallback"
        aria-disabled={disabled}
        {...rest}
      >
        {children}
      </span>
    );
  }

  const cleanProps = filterGeneratedProps(props);

  // Sanitize children when asChild is true to remove generated props
  const sanitizedChildren = asChild ? sanitizeTriggerChild(children) : children;

  return (
    <RadixDropdownMenu.Trigger
      ref={ref}
      data-slot="dropdown-menu-trigger"
      asChild={asChild}
      {...cleanProps}
    >
      {sanitizedChildren}
    </RadixDropdownMenu.Trigger>
  );
});

DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & NativeMetadataProps
>(({ className, sideOffset = 4, ...props }, ref) => {
  if (!hasRadixDropdownMenu) {
    warnMissingRadixDropdownMenu();
    const cleanProps = filterGeneratedProps(props);
    const { nativeProps, rest } = splitNativeProps(cleanProps);
    return (
      <div
        data-slot="dropdown-menu-content-fallback"
        data-native-role={nativeProps["data-native-role"] ?? "menu"}
        className={cn(
          "qs-vibrant-fill text-popover-foreground z-50 min-w-[8rem]",
          // Apple macOS 26 menu spec — 13 px outer radius, 12 px / 5 px
          // inner padding, layered drop shadow (no hairline border).
          "rounded-[var(--ui-menu-radius)] px-[var(--menu-padding-x)] py-[var(--menu-padding-y)] shadow-[var(--ui-menu-shadow)]",
          className,
        )}
        {...nativeProps}
        {...rest}
      />
    );
  }
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  const { style, ...restWithoutStyle } = rest as { style?: React.CSSProperties };
  return (
    <RadixDropdownMenu.Portal container={fullscreenPortalContainer()}>
      <RadixDropdownMenu.Content
        ref={ref}
        data-slot="dropdown-menu-content"
        data-native-role={nativeProps["data-native-role"] ?? "menu"}
        sideOffset={sideOffset}
        // Inline because `.qs-vibrant-fill` (globals.css, unlayered) sets
        // overflow: hidden and outranks the layered utility; a menu taller
        // than the room Radix measures for it must scroll, not clip.
        style={{ overflowY: 'auto', ...style }}
        className={cn(
          "qs-vibrant-fill text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-y-auto",
          "rounded-[var(--ui-menu-radius)] px-[var(--menu-padding-x)] py-[var(--menu-padding-y)] shadow-[var(--ui-menu-shadow)]",
          className,
        )}
        {...nativeProps}
        {...restWithoutStyle}
      />
    </RadixDropdownMenu.Portal>
  );
});

DropdownMenuContent.displayName = "DropdownMenuContent";

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.Group data-slot="dropdown-menu-group" {...cleanProps} />
  );
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  onClick,
  onSelect,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive";
  onClick?: (event: Event) => void;
} & NativeMetadataProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  
  // Radix only fires onSelect, so route both handlers through it. onSelect is
  // pulled out of the spread props: a caller passing `onSelect={undefined}`
  // (the header More menu does for ordinary rows) used to overwrite this
  // handler via {...rest}, which silently killed onClick. Both run, so a
  // caller can keep the menu open with onSelect's preventDefault and still
  // act in onClick.
  const handleSelect = (event: Event) => {
    onSelect?.(event);
    onClick?.(event);
  };
  
  return (
    <RadixDropdownMenu.Item
      data-slot="dropdown-menu-item"
      data-native-role={nativeProps["data-native-role"] ?? "menu-item"}
      data-inset={inset}
      data-variant={variant}
      className={cn(
        // The active row takes a faint wash, as ChatGPT's menus do; the label
        // and glyphs keep their colours. Keyed on hover + focus + Radix's
        // data-highlighted so it fires for mouse and keyboard alike.
        "hover:bg-accent focus:bg-accent data-[highlighted]:bg-accent data-[variant=destructive]:text-destructive data-[variant=destructive]:hover:bg-destructive/10 data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        // Row size, radius and type come from the --menu-* tokens. `min-h`,
        // not `h`: a label that wraps (narrow menu on a phone, "Suspend &
        // Revoke Access") must grow the row rather than paint over the next.
        "min-h-[var(--menu-item-height)] py-0.5 gap-[var(--menu-item-gap)] px-2.5 rounded-[var(--ui-menu-item-radius)] text-[length:var(--menu-font-size)] leading-tight font-normal",
        className,
      )}
      {...nativeProps}
      {...rest}
      onSelect={handleSelect}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      {...cleanProps}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <RadixDropdownMenu.ItemIndicator>
          <CheckIcon className="size-4" />
        </RadixDropdownMenu.ItemIndicator>
      </span>
      {children}
    </RadixDropdownMenu.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...cleanProps}
    />
  );
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "focus:bg-accent data-[highlighted]:bg-accent relative flex cursor-default items-center gap-[var(--menu-item-gap)] rounded-[var(--ui-menu-item-radius)] min-h-[var(--menu-item-height)] py-0.5 pr-2.5 pl-8 text-[length:var(--menu-font-size)] leading-tight font-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...cleanProps}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <RadixDropdownMenu.ItemIndicator>
          <CheckIcon className="size-3.5" />
        </RadixDropdownMenu.ItemIndicator>
      </span>
      {children}
    </RadixDropdownMenu.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "px-2 py-1.5 text-xs font-medium text-muted-foreground data-[inset]:pl-8",
        className,
      )}
      {...cleanProps}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.Separator
      data-slot="dropdown-menu-separator"
      // Apple macOS 26 menu separator — vibrant secondary fill,
      // 1 px hairline, breathing-room margins (5 px top/bottom).
      className={cn(
        "h-px",
        "bg-menu-separator -mx-[var(--menu-padding-x)] my-[var(--menu-padding-y)]",
        className,
      )}
      {...cleanProps}
    />
  );
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  const cleanProps = filterGeneratedProps(props);
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...cleanProps}
    />
  );
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  const cleanProps = filterGeneratedProps(props);
  return <RadixDropdownMenu.Sub data-slot="dropdown-menu-sub" {...cleanProps} />;
}

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => {
  const cleanProps = filterGeneratedProps(props);
  return (
    <RadixDropdownMenu.SubTrigger
      ref={ref}
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8",
        className,
      )}
      {...cleanProps}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </RadixDropdownMenu.SubTrigger>
  );
});

DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  const cleanProps = filterGeneratedProps(props);
  const { style, ...restWithoutStyle } = cleanProps as { style?: React.CSSProperties };
  // Portaled like the menu itself: rendered inside the parent menu it is
  // clipped by that menu's scrolling box the moment it extends past it,
  // which a submenu always does.
  return (
    <RadixDropdownMenu.Portal container={fullscreenPortalContainer()}>
      <RadixDropdownMenu.SubContent
        ref={ref}
        data-slot="dropdown-menu-sub-content"
        style={{ overflowY: 'auto', ...style }}
        className={cn(
          "qs-vibrant-fill text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-y-auto",
          "rounded-[var(--ui-menu-radius)] px-[var(--menu-padding-x)] py-[var(--menu-padding-y)] shadow-[var(--ui-menu-shadow)]",
          className,
        )}
        {...restWithoutStyle}
      />
    </RadixDropdownMenu.Portal>
  );
});

DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

/** Caret pointing back at the trigger, so an anchored menu reads as a callout
 *  rather than a slab floating beside it. Inherits the menu's own surface via
 *  `fill`, which callers set to the popover token. */
function DropdownMenuArrow({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Arrow>) {
  if (!hasRadixDropdownMenu || typeof (RadixDropdownMenu as any)?.Arrow === "undefined") {
    return null;
  }
  return (
    <DropdownMenuPrimitive.Arrow
      data-slot="dropdown-menu-arrow"
      width={12}
      height={6}
      className={className}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuArrow,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
